import React, { useEffect, useState, useRef } from 'react';
import logoImage from '../../images/Logo_addox.png';

const LoadingScreen = ({ onComplete, onTransitionComplete }) => {
    const [phase, setPhase] = useState(0); // 0: Welcome, 1: Logo fade in, 2: Transition, 3: Complete
    const [welcomeText, setWelcomeText] = useState(''); // Typewriter effect for "WELCOME TO"
    const logoRef = useRef(null);
    const textRef = useRef(null);
    const virtualIntelligenceRef = useRef(null);
    const containerRef = useRef(null);
    const scrollPreventionRef = useRef(null); // Store scroll prevention cleanup function
    const isTransitioningRef = useRef(false); // Prevent multiple transitions
    const transitionStartedRef = useRef(false); // Track if transition has started
    const isMountedRef = useRef(true); // Track if component is mounted

    // Disable scrolling while loading screen is active - more aggressive prevention
    useEffect(() => {
        // Store original values
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const originalBodyPosition = document.body.style.position;
        const originalBodyTop = document.body.style.top;
        const originalBodyWidth = document.body.style.width;
        
        // Get current scroll position
        const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
        
        // Disable scrolling with CSS - multiple methods for maximum prevention
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.documentElement.style.height = '100%';
        
        // Prevent scroll via all possible methods
        const preventScroll = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };
        
        // Prevent keyboard scrolling (arrow keys, space, page up/down, etc.)
        const preventKeyboardScroll = (e) => {
            const scrollKeys = [32, 33, 34, 35, 36, 37, 38, 39, 40]; // space, page up/down, home, end, arrows
            if (scrollKeys.includes(e.keyCode) || scrollKeys.includes(e.which)) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };
        
        // Force scroll position to stay at saved position - optimized to only act on significant changes
        const forceScrollPosition = () => {
            const currentScroll = window.scrollY || document.documentElement.scrollTop || 0;
            if (Math.abs(currentScroll - scrollY) > 1) {
                window.scrollTo(0, scrollY);
            }
        };
        
        // Options for event listeners - use capture phase for better prevention
        const eventOptions = { passive: false, capture: true };
        
        // Add event listeners at multiple levels for maximum prevention
        // Window level
        window.addEventListener('wheel', preventScroll, eventOptions);
        window.addEventListener('touchmove', preventScroll, eventOptions);
        window.addEventListener('touchstart', preventScroll, eventOptions);
        window.addEventListener('scroll', forceScrollPosition, eventOptions);
        window.addEventListener('keydown', preventKeyboardScroll, eventOptions);
        
        // Document level
        document.addEventListener('wheel', preventScroll, eventOptions);
        document.addEventListener('touchmove', preventScroll, eventOptions);
        document.addEventListener('touchstart', preventScroll, eventOptions);
        document.addEventListener('scroll', forceScrollPosition, eventOptions);
        document.addEventListener('keydown', preventKeyboardScroll, eventOptions);
        
        // Body level
        document.body.addEventListener('wheel', preventScroll, eventOptions);
        document.body.addEventListener('touchmove', preventScroll, eventOptions);
        document.body.addEventListener('touchstart', preventScroll, eventOptions);
        
        // Continuously monitor and prevent scrolling - optimized frequency (reduced from 10ms to 50ms)
        const scrollMonitor = setInterval(() => {
            // Check if container is still visible - if so, keep scrolling disabled
            if (containerRef.current) {
                const computedStyle = window.getComputedStyle(containerRef.current);
                const opacity = parseFloat(computedStyle.opacity);
                const pointerEvents = computedStyle.pointerEvents;
                
                // If container is still visible (opacity > 0.1 or pointer-events not 'none'), keep scrolling disabled
                if (opacity > 0.1 || pointerEvents !== 'none') {
                    // Force scroll position to stay at saved position - only if changed significantly
                    const currentScroll = window.scrollY || document.documentElement.scrollTop || 0;
                    if (Math.abs(currentScroll - scrollY) > 1) {
                        window.scrollTo(0, scrollY);
                    }
                }
            } else {
                // Container not found, still prevent scrolling - only if changed significantly
                const currentScroll = window.scrollY || document.documentElement.scrollTop || 0;
                if (Math.abs(currentScroll - scrollY) > 1) {
                    window.scrollTo(0, scrollY);
                }
            }
        }, 50); // Reduced from 10ms to 50ms for better performance
        
        // Re-enable scrolling function
        const enableScrolling = () => {
            // Clear interval
            clearInterval(scrollMonitor);
            
            // Remove all event listeners
            window.removeEventListener('wheel', preventScroll, eventOptions);
            window.removeEventListener('touchmove', preventScroll, eventOptions);
            window.removeEventListener('touchstart', preventScroll, eventOptions);
            window.removeEventListener('scroll', forceScrollPosition, eventOptions);
            window.removeEventListener('keydown', preventKeyboardScroll, eventOptions);
            
            document.removeEventListener('wheel', preventScroll, eventOptions);
            document.removeEventListener('touchmove', preventScroll, eventOptions);
            document.removeEventListener('touchstart', preventScroll, eventOptions);
            document.removeEventListener('scroll', forceScrollPosition, eventOptions);
            document.removeEventListener('keydown', preventKeyboardScroll, eventOptions);
            
            document.body.removeEventListener('wheel', preventScroll, eventOptions);
            document.body.removeEventListener('touchmove', preventScroll, eventOptions);
            document.body.removeEventListener('touchstart', preventScroll, eventOptions);
            
            // Restore original styles
            document.body.style.overflow = originalBodyOverflow;
            document.body.style.position = originalBodyPosition;
            document.body.style.top = originalBodyTop;
            document.body.style.width = originalBodyWidth;
            document.body.style.left = '';
            document.body.style.right = '';
            document.documentElement.style.overflow = originalHtmlOverflow;
            document.documentElement.style.height = '';
            
            // Restore scroll position after a brief delay using requestAnimationFrame for smoother transition
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    window.scrollTo(0, scrollY);
                });
            });
        };
        
        // Store cleanup function in ref for use in other effects
        scrollPreventionRef.current = enableScrolling;
        
        // Cleanup: re-enable scrolling on unmount
        return () => {
            enableScrolling();
            scrollPreventionRef.current = null;
        };
    }, []);

    // Re-enable scrolling when transition completes
    useEffect(() => {
        if (phase === 3 && scrollPreventionRef.current) {
            // Phase 3 means complete, re-enable scrolling using the stored cleanup function
            scrollPreventionRef.current();
            scrollPreventionRef.current = null;
        }
    }, [phase]);

    // Typewriter effect for "WELCOME TO" - optimized for speed
    useEffect(() => {
        const fullText = 'WELCOME TO';
        let currentIndex = 0;
        
        const typewriterInterval = setInterval(() => {
            if (currentIndex < fullText.length) {
                setWelcomeText(fullText.substring(0, currentIndex + 1));
                currentIndex++;
            } else {
                clearInterval(typewriterInterval);
            }
        }, 50); // 50ms per character - optimized for faster display
        
        return () => clearInterval(typewriterInterval);
    }, []);

    useEffect(() => {
        // Mark component as mounted
        isMountedRef.current = true;
        
        // Helper function to check if page is ready
        const isPageReady = () => {
            return document.readyState === 'complete' && 
                   (document.querySelector('[data-landing-logo]') || 
                    document.querySelector('#landing-page'));
        };

        // Helper function to find elements with retry logic and timeout
        const findElementsWithRetry = (maxRetries = 10, delay = 100, maxWaitTime = 1000) => {
            return new Promise((resolve) => {
                let attempts = 0;
                const startTime = Date.now();
                
                // Timeout to prevent infinite waiting
                const timeoutId = setTimeout(() => {
                    // Resolve with whatever we found (or null) after maxWaitTime
                    const landingLogo = document.querySelector('[data-landing-logo]');
                    const landingSection = document.querySelector('#landing-page');
                    const landingHeading = landingSection?.querySelector('h1');
                    const allParagraphs = landingSection?.querySelectorAll('p');
                    let landingVirtualIntelligence = null;
                    
                    if (allParagraphs) {
                        allParagraphs.forEach(p => {
                            if (p.textContent.trim() === 'VIRTUAL INTELLIGENCE') {
                                landingVirtualIntelligence = p.parentElement;
                            }
                        });
                    }
                    resolve({ landingLogo, landingHeading, landingVirtualIntelligence });
                }, maxWaitTime);
                
                const tryFind = () => {
                    attempts++;
                    const landingLogo = document.querySelector('[data-landing-logo]');
                    const landingSection = document.querySelector('#landing-page');
                    const landingHeading = landingSection?.querySelector('h1');
                    const allParagraphs = landingSection?.querySelectorAll('p');
                    let landingVirtualIntelligence = null;
                    
                    if (allParagraphs) {
                        allParagraphs.forEach(p => {
                            if (p.textContent.trim() === 'VIRTUAL INTELLIGENCE') {
                                landingVirtualIntelligence = p.parentElement;
                            }
                        });
                    }
                    
                    // If elements found or max retries reached, resolve
                    // Also check if page is ready or timeout reached
                    const elapsed = Date.now() - startTime;
                    if ((landingLogo && landingHeading) || attempts >= maxRetries || isPageReady() || elapsed >= maxWaitTime) {
                        clearTimeout(timeoutId);
                        resolve({ landingLogo, landingHeading, landingVirtualIntelligence });
                    } else {
                        setTimeout(tryFind, delay);
                    }
                };
                tryFind();
            });
        };

        // Wait for page to be fully loaded before starting transitions (prevents stuck loading on refresh)
        const startTransitions = () => {
            // Prevent multiple transitions from starting
            if (transitionStartedRef.current || isTransitioningRef.current) {
                return null;
            }
            
            transitionStartedRef.current = true;
            isTransitioningRef.current = true;
            
            // Fixed timing for stable transitions
            // Phase 1: Show "WELCOME TO" with typewriter effect
            const timer1 = setTimeout(() => {
                if (!containerRef.current) return; // Safety check
                setPhase(1);
            }, 520); // Optimized timing - typewriter takes ~500ms (10 chars × 50ms), minimal pause

            // Phase 2: Show logo and "TheAdox.com" - fixed timing
            const timer2 = setTimeout(() => {
                if (!containerRef.current) return; // Safety check
                setPhase(2);
                
                // Get target positions from landing page - with retry logic for stability
                // Use fixed delay instead of variable timing
                setTimeout(async () => {
                    // Safety check - ensure we're still transitioning
                    if (!isTransitioningRef.current || !containerRef.current) {
                        return;
                    }
                    
                    // Wait for elements to be ready with retry logic (max 500ms wait for faster response)
                    const { landingLogo, landingHeading, landingVirtualIntelligence } = await findElementsWithRetry(5, 100, 500);
                    
                    // Safety check again after async operation
                    if (!isTransitioningRef.current || !containerRef.current || !isMountedRef.current) {
                        return;
                    }
                    
                    // Fixed transition delay for consistent timing
                    const transitionDelay = 1000; // Reduced delay for faster loading
                    
                    // Use single requestAnimationFrame for better performance
                    requestAnimationFrame(() => {
                        // Safety check
                        if (!isTransitioningRef.current || !containerRef.current || !isMountedRef.current) {
                            return;
                        }
                        
                        // Always attempt transition, even if elements not found
                        try {
                            
                            if (landingLogo && logoRef.current && isTransitioningRef.current) {
                                try {
                                    const targetRect = landingLogo.getBoundingClientRect();
                                    const sourceRect = logoRef.current.getBoundingClientRect();
                                    
                                    // Validate rects are valid
                                    if (targetRect.width > 0 && targetRect.height > 0 && 
                                        sourceRect.width > 0 && sourceRect.height > 0 &&
                                        !isNaN(targetRect.left) && !isNaN(targetRect.top) &&
                                        !isNaN(sourceRect.left) && !isNaN(sourceRect.top)) {
                                        const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
                                        const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);
                                        const scaleX = targetRect.width / sourceRect.width;
                                        const scaleY = targetRect.height / sourceRect.height;
                                        
                                        // Use will-change for better performance
                                        logoRef.current.style.willChange = 'transform';
                                        logoRef.current.style.transition = 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)';
                                        logoRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${Math.min(scaleX, scaleY)})`;
                                    }
                                } catch (e) {
                                    // Silently handle errors to prevent breaking transition
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn('Logo transition error:', e);
                                    }
                                }
                            }
                        
                            if (landingHeading && textRef.current && isTransitioningRef.current) {
                                try {
                                    const targetRect = landingHeading.getBoundingClientRect();
                                    const sourceRect = textRef.current.getBoundingClientRect();
                                    
                                    // Validate rects are valid
                                    if (targetRect.width > 0 && targetRect.height > 0 && 
                                        sourceRect.width > 0 && sourceRect.height > 0 &&
                                        !isNaN(targetRect.left) && !isNaN(targetRect.top) &&
                                        !isNaN(sourceRect.left) && !isNaN(sourceRect.top)) {
                                        const computedStyle = window.getComputedStyle(landingHeading);
                                        const targetFontSize = computedStyle.fontSize;
                                        const targetLineHeight = computedStyle.lineHeight;
                                        const targetLetterSpacing = computedStyle.letterSpacing;
                                        
                                        // Use will-change for better performance
                                        textRef.current.style.willChange = 'transform, font-size, line-height, letter-spacing';
                                        // Set transition first
                                        textRef.current.style.transition = 'transform 1s cubic-bezier(0.4, 0, 0.2, 1), font-size 1s cubic-bezier(0.4, 0, 0.2, 1), line-height 1s cubic-bezier(0.4, 0, 0.2, 1), letter-spacing 1s cubic-bezier(0.4, 0, 0.2, 1)';
                                        
                                        // Calculate position based on current size, then apply font size and position together
                                        const deltaX = targetRect.left - sourceRect.left;
                                        const deltaY = targetRect.top - sourceRect.top;
                                        
                                        // Apply font size and position simultaneously
                                        textRef.current.style.fontSize = targetFontSize;
                                        textRef.current.style.lineHeight = targetLineHeight;
                                        textRef.current.style.letterSpacing = targetLetterSpacing;
                                        textRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
                                        
                                        // Adjust position after font size change
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                if (textRef.current && landingHeading) {
                                                    try {
                                                        const newSourceRect = textRef.current.getBoundingClientRect();
                                                        const newTargetRect = landingHeading.getBoundingClientRect();
                                                        if (newSourceRect.width > 0 && newTargetRect.width > 0 &&
                                                            !isNaN(newSourceRect.left) && !isNaN(newTargetRect.left)) {
                                                            const adjustedDeltaX = newTargetRect.left - newSourceRect.left;
                                                            const adjustedDeltaY = newTargetRect.top - newSourceRect.top;
                                                            textRef.current.style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
                                                        }
                                                    } catch (e) {
                                                        // Silently fail if adjustment fails
                                                    }
                                                }
                                            });
                                        });
                                    }
                                } catch (e) {
                                    // Silently handle errors to prevent breaking transition
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn('Text transition error:', e);
                                    }
                                }
                            }
                        
                            if (landingVirtualIntelligence && virtualIntelligenceRef.current && isTransitioningRef.current) {
                                try {
                                    const targetRect = landingVirtualIntelligence.getBoundingClientRect();
                                    const sourceRect = virtualIntelligenceRef.current.getBoundingClientRect();
                                    
                                    // Validate rects are valid
                                    if (targetRect.width > 0 && targetRect.height > 0 && 
                                        sourceRect.width > 0 && sourceRect.height > 0 &&
                                        !isNaN(targetRect.left) && !isNaN(targetRect.top) &&
                                        !isNaN(sourceRect.left) && !isNaN(sourceRect.top)) {
                                        const targetP = landingVirtualIntelligence.querySelector('p');
                                        const computedStyle = targetP ? window.getComputedStyle(targetP) : null;
                                        const targetFontSize = computedStyle?.fontSize;
                                        const targetLetterSpacing = computedStyle?.letterSpacing;
                                        
                                        // Use will-change for better performance
                                        virtualIntelligenceRef.current.style.willChange = 'transform';
                                        // Set transition first
                                        virtualIntelligenceRef.current.style.transition = 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)';
                                        
                                        const deltaX = targetRect.left - sourceRect.left;
                                        const deltaY = targetRect.top - sourceRect.top;
                                        
                                        // Apply font size changes - slower transition
                                        if (targetFontSize) {
                                            const pElement = virtualIntelligenceRef.current.querySelector('p');
                                            if (pElement) {
                                                pElement.style.transition = 'font-size 1s cubic-bezier(0.4, 0, 0.2, 1), letter-spacing 1s cubic-bezier(0.4, 0, 0.2, 1)';
                                                pElement.style.fontSize = targetFontSize;
                                                if (targetLetterSpacing) {
                                                    pElement.style.letterSpacing = targetLetterSpacing;
                                                }
                                            }
                                        }
                                        
                                        // Apply position
                                        virtualIntelligenceRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
                                        
                                        // Adjust position after font size change
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                if (virtualIntelligenceRef.current && landingVirtualIntelligence) {
                                                    try {
                                                        const newSourceRect = virtualIntelligenceRef.current.getBoundingClientRect();
                                                        const newTargetRect = landingVirtualIntelligence.getBoundingClientRect();
                                                        if (newSourceRect.width > 0 && newTargetRect.width > 0 &&
                                                            !isNaN(newSourceRect.left) && !isNaN(newTargetRect.left)) {
                                                            const adjustedDeltaX = newTargetRect.left - newSourceRect.left;
                                                            const adjustedDeltaY = newTargetRect.top - newSourceRect.top;
                                                            virtualIntelligenceRef.current.style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
                                                        }
                                                    } catch (e) {
                                                        // Silently fail if adjustment fails
                                                    }
                                                }
                                            });
                                        });
                                    }
                                } catch (e) {
                                    // Silently handle errors to prevent breaking transition
                                    if (process.env.NODE_ENV === 'development') {
                                        console.warn('Virtual Intelligence transition error:', e);
                                    }
                                }
                            }
                        
                        } catch (error) {
                            // Silently handle errors to prevent breaking transition
                            if (process.env.NODE_ENV === 'development') {
                                console.warn('Loading screen transition error:', error);
                            }
                        } finally {
                            // Always complete transition (faster if no elements found, slower if elements exist)
                            setTimeout(() => {
                                // Safety check
                                if (!containerRef.current) return;
                                
                                // Mark transition as complete
                                isTransitioningRef.current = false;
                                
                                // Re-enable scrolling when transition completes using stored cleanup function
                                if (scrollPreventionRef.current) {
                                    scrollPreventionRef.current();
                                    scrollPreventionRef.current = null;
                                }
                                
                                if (onTransitionComplete) {
                                    onTransitionComplete();
                                }
                                if (containerRef.current) {
                                    containerRef.current.style.transition = 'opacity 0.4s ease-out'; // Faster fade out
                                    containerRef.current.style.opacity = '0';
                                    containerRef.current.style.pointerEvents = 'none';
                                }
                            }, transitionDelay); // Use dynamic delay based on whether elements were found
                        }
                    });
                }, 200); // Reduced delay for faster timing
        }, 800); // Optimized timing: 520ms phase1 + 280ms delay = 800ms

            // Complete after transition - fixed timing for stability
        const timer3 = setTimeout(() => {
            if (onComplete) {
                onComplete();
            }
            }, 2200); // Optimized timing: 520ms phase1 + 280ms delay + 1000ms transition + 400ms buffer

            // Maximum safety timeout - ensures loading screen always fades out
            // Fixed timeout for consistent behavior
        const safetyTimeout = setTimeout(() => {
                // Mark transition as complete
                isTransitioningRef.current = false;
                
                // Re-enable scrolling when safety timeout triggers using stored cleanup function
                if (scrollPreventionRef.current) {
                    scrollPreventionRef.current();
                    scrollPreventionRef.current = null;
                }
                
                // Force complete transition regardless of state
            if (onTransitionComplete) {
                onTransitionComplete();
            }
            if (containerRef.current) {
                    containerRef.current.style.transition = 'opacity 0.4s ease-out';
                containerRef.current.style.opacity = '0';
                containerRef.current.style.pointerEvents = 'none';
            }
                // Force phase to complete
                setPhase(3);
            }, 3000); // Optimized 3 seconds max - ensures loading screen never gets stuck with consistent timing

            // Store timers for cleanup
            // Note: transitionTimer is nested inside timer2, so clearing timer2 will prevent it from executing
            const timers = { timer1, timer2, timer3, safetyTimeout };
            return timers;
        };

        // Start transitions immediately if page is already loaded, otherwise wait for load event
        let timers;
        let initTimer;
        
        // Reset transition flags on mount
        transitionStartedRef.current = false;
        isTransitioningRef.current = false;
        
        const initializeTransitions = () => {
            // Prevent multiple initializations
            if (transitionStartedRef.current || timers) {
                return;
            }
            
            // Ensure container exists
            if (!containerRef.current) {
                // Retry after a short delay if container not ready
                initTimer = setTimeout(initializeTransitions, 50);
                return;
            }
            
            timers = startTransitions();
        };
        
        // Fixed initialization delay for consistent timing
        const INIT_DELAY = 100; // Fixed 100ms delay for DOM readiness
        
        if (document.readyState === 'complete' || isPageReady()) {
            // Fixed delay to ensure DOM is fully ready
            initTimer = setTimeout(() => {
                initializeTransitions();
            }, INIT_DELAY);
        } else {
            const handleLoad = () => {
                if (initTimer) clearTimeout(initTimer);
                initializeTransitions();
            };
            window.addEventListener('load', handleLoad, { once: true });
            
            // Fallback: start after fixed time even if load event doesn't fire
            // Fixed timeout for consistent behavior
            const fallbackTimer = setTimeout(() => {
                if (!timers && !transitionStartedRef.current) {
                    initializeTransitions();
                }
            }, 1000); // Fixed 1 second fallback

            return () => {
                window.removeEventListener('load', handleLoad);
                if (initTimer) clearTimeout(initTimer);
                clearTimeout(fallbackTimer);
                if (timers) {
                    clearTimeout(timers.timer1);
                    clearTimeout(timers.timer2);
                    clearTimeout(timers.timer3);
                    clearTimeout(timers.safetyTimeout);
                }
                // Reset flags on cleanup
                transitionStartedRef.current = false;
                isTransitioningRef.current = false;
            };
        }

        return () => {
            // Mark component as unmounted
            isMountedRef.current = false;
            
            if (initTimer) clearTimeout(initTimer);
            if (timers) {
                clearTimeout(timers.timer1);
                clearTimeout(timers.timer2);
                clearTimeout(timers.timer3);
                clearTimeout(timers.safetyTimeout);
            }
            // Reset flags on cleanup
            transitionStartedRef.current = false;
            isTransitioningRef.current = false;
        };
    }, [onComplete, onTransitionComplete]);

    return (
        <div 
            ref={containerRef}
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
            style={{
                background: 'transparent'
            }}
        >
            {/* Welcome Text with Typewriter Effect */}
            <div 
                className={`absolute transition-all duration-500 ${
                    phase >= 1 
                        ? 'opacity-0 translate-y-[-30px] pointer-events-none' 
                        : 'opacity-100 translate-y-0'
                }`}
            >
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white uppercase tracking-wider">
                    {welcomeText}
                    <span className="inline-block w-1 h-full bg-white ml-1 animate-pulse">|</span>
                </h1>
            </div>

            {/* Logo and TheAdox.com - Separate for independent transitions */}
            <div className="flex flex-col items-center justify-center">
                {/* Logo */}
                <div 
                    ref={logoRef}
                    data-loading-logo="true"
                    className={`mb-6 relative transition-all duration-500 ${
                        phase >= 1 
                            ? 'opacity-100 translate-y-0 scale-100' 
                            : 'opacity-0 translate-y-[30px] scale-95'
                    }`}
                    style={{
                        willChange: phase >= 2 ? 'transform, opacity' : 'auto'
                    }}
                >
                    <div className={`relative ${phase >= 2 ? '' : 'animate-[logoFloat_3s_ease-in-out_infinite,logoPulse_2s_ease-in-out_infinite]'}`}>
                        <img 
                            src={logoImage} 
                            alt="The Adox Logo" 
                            className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 object-contain relative z-10"
                            style={{
                                filter: 'drop-shadow(0 0 30px rgba(230, 138, 64, 0.8))',
                            }}
                        />
                        {/* Glow effect */}
                        <div className={`absolute inset-0 bg-[#E68A40]/20 rounded-full blur-3xl ${phase >= 2 ? '' : 'animate-[logoGlow_2s_ease-in-out_infinite]'} -z-10`}></div>
                    </div>
                </div>

                {/* TheAdox Text */}
                <div 
                    ref={textRef}
                    className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold leading-[0.9] tracking-tight transition-all duration-500 ${
                        phase >= 1 
                            ? 'opacity-100 translate-y-0 scale-100' 
                            : 'opacity-0 translate-y-[30px] scale-95'
                    }`}
                    style={{
                        willChange: phase >= 2 ? 'transform, opacity, font-size, line-height, letter-spacing' : 'auto',
                        transformOrigin: 'left center'
                    }}
                >
                    <span className="text-[#E68A40]">The</span><span className="text-white">Adox.com</span>
                </div>

                {/* VIRTUAL INTELLIGENCE Text */}
                <div 
                    ref={virtualIntelligenceRef}
                    className={`mt-4 transition-all duration-500 ${
                        phase >= 1 
                            ? 'opacity-100 translate-y-0 scale-100' 
                            : 'opacity-0 translate-y-[30px] scale-95'
                    }`}
                    style={{
                        willChange: phase >= 2 ? 'transform, opacity' : 'auto',
                        transformOrigin: 'center center'
                    }}
                >
                    <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white font-normal tracking-[0.15em] uppercase whitespace-nowrap">
                        VIRTUAL INTELLIGENCE
                    </p>
                </div>
            </div>

        </div>
    );
};

export default LoadingScreen;

